import { useContext, useEffect } from "react";

import { MktgDataContext, useAppSetting } from "@swa-ui/application";
import { useLocation } from "@swa-ui/browser";
import { swaDate } from "@swa-ui/date";

const MIN_DAYS_DEPARTURE_TIME_DATA = 0;
const MAX_DAYS_DEPARTURE_TIME_DATA = 2;
const pnrWheelchairAssistance = ["NEED_LIFT", "NEED_ASSISTANCE"];

const analyticsPnrType = {
  COMPANION: { pnr_with_companion: "1" },
  GDS: { pnr_booked_with_gds: "1" },
  NON_REVENUE: { isnonrev: "1" },
};

const ALLOWED_EQUIPMENT_TYPES = ["7M8", "7T8"];
const CONVERT_TO_SECONDS = 60;
const CONVERT_TO_MILLI_SECONDS = 1000;
const SCHEDULED_DEPART_TIME_MIN_CUTOFF = 60;

export function useViewPageAnalytics(
  reservations,
  reservationSearchToken,
  departUpgradePriceDiff = 0,
  arriveUpgradePriceDiff = 0
) {
  const {
    associated_reservations = [],
    bounds = [],
    disrupted = false,
    id: pnrId,
    passengers = [],
    permissions = {},
    pnr_digital_channel_source = "",
    pnr_type = "",
    pnr_sub_type = "",
    trip_type = "",
  } = reservations ?? {};
  const { can_upgrade } = permissions;
  const { pathname } = useLocation();
  const { setMktgPageData } = useContext(MktgDataContext);
  const isVacation = pnr_sub_type === "SOUTHWEST_GETAWAYS";
  const scheduledDepartTimeMaxCutoff = useAppSetting("scheduledDepartTimeMaxCutoff");
  const lowestUpgradePrice = Math.min(departUpgradePriceDiff, arriveUpgradePriceDiff);

  useEffect(() => {
    if (pathname.includes("/air/manage-reservation/view")) {
      let upgradedBoardingELR = "0";

      if (bounds && passengers && hasUpgradedBoarding(passengers)) {
        const boundNotFlown = bounds?.filter((bound) => !bound?.flown)?.[0] ?? {};
        const segments = boundNotFlown?.segments ?? [];
        const containsEligibleTimeRangeForUpgradedBoarding = isEligibleTimeRangeForUB(segments);

        if (containsEligibleTimeRangeForUpgradedBoarding) {
          upgradedBoardingELR = "1";
        }
      }

      const viewPagePNRAnalyticsObject = {
        ...analyticsPnrType[pnr_type],
        ...(disrupted && { isreaccom: "1" }),
        ...(can_upgrade && { upsell_shown: "1" }),
        domain_name: window.location.host,
        ...(hasInternationalBound() && { pnr_international: "1" }),
        ...(isEarlyBirdPurchased() && { pnr_eb: "1" }),
        ...(isMinorUnaccompanied() && { pnr_unaccompanied_minor: "1" }),
        ...(isWheelchairNeeded() && { pnr_wheelchair: "1" }),
        ...(pnr_digital_channel_source === "CORPORATE" && { pnr_swabiz: "1" }),
        searchToken: reservationSearchToken || "",
        ...(isVacation && { pnrisvacation: "1" }),
        upgradeboarding_elr_available: upgradedBoardingELR,
        vacationbannerdisplayed: isVacation ? "1" : "0",
        ...(lowestUpgradePrice && { upsell_lowestpricecurrency: lowestUpgradePrice.toString() }),
      };

      setMktgPageData(
        {
          air_daystotrip: daysUntilNextFlight(),
          pnr: pnrId ?? "",
          trip_type: getTripType(trip_type?.toLowerCase()),
          trip_status: getTripStatus(),
          ...viewPagePNRAnalyticsObject,
          ...getPassengersObject(),
          ...getTripDetails(),
        },
        "viewPage"
      );
    }
  }, [pathname, reservations, reservationSearchToken]);

  function hasUpgradedBoarding(passengerData) {
    const allSegmentsFeatures = passengerData.flatMap((passenger) => passenger.segment_features);
    let isUpgradedBoardingEligible = false;

    allSegmentsFeatures.some((segment) => {
      if (segment?.ancillaries_available?.includes("UPGRADED_BOARDING")) {
        isUpgradedBoardingEligible = true;
      }
    });

    return isUpgradedBoardingEligible;
  }

  function isEligibleTimeRangeForUB(segments) {
    const departureTimeStr = segments?.[0].depart_at;
    const departureTime = new Date(departureTimeStr);
    const currentDate = new Date();
    const timeDifferenceInMilliseconds = departureTime - currentDate;
    const maxDifferenceInMilliseconds =
      scheduledDepartTimeMaxCutoff * CONVERT_TO_SECONDS * CONVERT_TO_MILLI_SECONDS;
    const minDifferenceInMilliseconds =
      SCHEDULED_DEPART_TIME_MIN_CUTOFF * CONVERT_TO_SECONDS * CONVERT_TO_MILLI_SECONDS;
    const isWithinCutoff =
      timeDifferenceInMilliseconds > 0 &&
      timeDifferenceInMilliseconds <= maxDifferenceInMilliseconds &&
      timeDifferenceInMilliseconds > minDifferenceInMilliseconds;
    const hasAllowedType = segments?.some((segmentData) =>
      ALLOWED_EQUIPMENT_TYPES.includes(segmentData.flight_equipment_type_code)
    );

    return isWithinCutoff && hasAllowedType;
  }

  function getPassengersObject() {
    const passengerAges = {
      ADULT: 0,
      INFANT_IN_SEAT: 0,
      LAP_CHILD: 0,
      MINOR: 0,
      YOUTH: 0,
    };
    let includesSeats = false;

    passengers?.forEach((passenger) => {
      const passengerAgeType = passenger?.age_type;

      includesSeats = passenger.seat_info?.some((seat) => !!seat.seat_number) ?? false;
      passengerAges[passengerAgeType] = passengerAges[passengerAgeType] + 1;

      if (passenger?.lap_child_name) {
        passengerAges.LAP_CHILD = passengerAges.LAP_CHILD + 1;
      }
    });

    return {
      pax_adult: `${passengerAges.ADULT}`,
      pax_child_0_4: `${passengerAges.INFANT_IN_SEAT}`,
      pax_child_5_11: `${passengerAges.MINOR}`,
      pax_lapchild: `${passengerAges.LAP_CHILD}`,
      pax_teen: `${passengerAges.YOUTH}`,
      ...(includesSeats ? { seatsassigned: "1" } : {}),
    };
  }

  function getTripStatus() {
    const nowInUtc = swaDate.utc().format("YYYY-MM-DD HH:mm");
    let tripStatus = "";

    bounds?.forEach((bound) => {
      const segments = bound?.segments;
      const departureTime = swaDate.utc(segments[0]?.depart_at).format("YYYY-MM-DD HH:mm");

      if (tripStatus !== "inprogress") {
        tripStatus = !bound?.flown
          ? departureTime > nowInUtc
            ? "upcoming"
            : "inprogress"
          : "completed";
      }
    });

    return tripStatus;
  }

  function daysUntilNextFlight() {
    const today = swaDate();
    const notFlownFlights = bounds?.filter((bound) => !bound?.flown);
    const nextFlight = notFlownFlights[0];
    let result = "";

    if (nextFlight) {
      const departDate = swaDate(nextFlight?.segments[0]?.depart_at);
      const daysRemaining = Math.floor(swaDate(departDate).diff(swaDate(today), "days"));

      if (daysRemaining === 0) {
        result = "zero";
      } else {
        result = daysRemaining > 0 ? daysRemaining.toString() : "";
      }
    }

    return result;
  }

  function getTripType(trip) {
    let tripType = "MC";

    if (trip === "roundtrip") {
      tripType = "RT";
    } else if (trip === "oneway") {
      tripType = "OW";
    }

    return tripType;
  }

  function hasInternationalBound() {
    return bounds?.some((bound) => bound?.international === true);
  }

  function hasMinorTraveler() {
    return passengers?.some((passenger) => passenger?.age_type === "MINOR");
  }

  function isEarlyBirdPurchased() {
    return passengers?.some((passenger) =>
      passenger?.segment_features?.some((segmentFeature) =>
        segmentFeature?.ancillaries_purchased?.includes("AUTO_CHECKIN")
      )
    );
  }

  function isMinorUnaccompanied() {
    return (
      hasMinorTraveler() &&
      !associated_reservations?.some((reservation) => reservation?.type === "ADULT")
    );
  }

  function isWheelchairNeeded() {
    return passengers?.some((passenger) =>
      pnrWheelchairAssistance.includes(passenger?.special_services?.wheelchair_assistance)
    );
  }

  function getTripDetails() {
    const boundNotFlown = bounds?.filter((bound) => !bound?.flown)?.[0] ?? {};
    const segments = boundNotFlown?.segments ?? [];
    const tripData = {};

    if (segments.length) {
      tripData.departuredate = `${swaDate(segments?.[0]?.depart_at)?.format("YYYY-MM-DD")}`;
      tripData.departuretime = `${swaDate(segments?.[0]?.depart_at)?.format("HH:mm")}`;
      tripData.air_bound_airportcode = `${boundNotFlown?.origination_airport_code}:${boundNotFlown?.destination_airport_code}`;
      tripData.segmentairports = getSegmentAirports(segments);
      tripData.fare = segments?.[0]?.fare_family;
      tripData.customer_minutestodeparture = getMinutesToDeparture(segments?.[0]);
      tripData.checkin_status = boundNotFlown?.checked_in ? "1" : "0";
    }

    return tripData;
  }

  function getSegmentAirports(segments) {
    return segments
      .map((segment) => `${segment.origination_airport_code}:${segment.destination_airport_code}`)
      .join(",");
  }

  function getMinutesToDeparture(segment) {
    let result = "";

    if (segment) {
      const today = swaDate();
      const departureDate = swaDate(segment.depart_at);
      const daysRemaining = Math.floor(swaDate(departureDate).diff(swaDate(today), "days"));
      const minutesRemaining = Math.floor(swaDate(departureDate).diff(swaDate(today), "minutes"));

      result =
        daysRemaining >= MIN_DAYS_DEPARTURE_TIME_DATA &&
        daysRemaining <= MAX_DAYS_DEPARTURE_TIME_DATA
          ? minutesRemaining.toString()
          : "";
    }

    return result;
  }
}
